import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Alert
} from '@mui/material';
import Draggable from 'react-draggable';

const EADraggablePaper = React.forwardRef((props, ref) => {
  const nodeRef = React.useRef(null);
  const setRefs = React.useCallback(
    (node) => {
      nodeRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref]
  );
  return (
    <Draggable
      nodeRef={nodeRef}
      handle=".ea-draggable-title"
      cancel={'[class*="MuiDialogContent-root"]'}
      bounds="window"
      onStart={(e) => {
        // Prevent parent modals from dragging
        e.stopPropagation();
      }}
    >
      <Paper {...props} ref={setRefs} />
    </Draggable>
  );
});

const EACrossModal = ({ open, onClose, invoice, userId, supabase, baseAmount, onApplyCross }) => {
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && invoice?.num_identificacion && userId) {
      loadEaEntries();
    }
  }, [open, invoice, userId]);

  const loadEaEntries = async () => {
    setLoading(true);
    setError('');
    setSelectedIds([]);
    try {
      const nitStr = invoice.num_identificacion.toString().trim();
      const nitSearch = nitStr.includes('-') ? nitStr.split('-')[0].trim() : nitStr;

      const { data, error: err } = await supabase
        .from('invoice_ea_entries')
        .select('*')
        .eq('tenant_id', userId)
        .eq('id_n', nitSearch)
        .gt('cost_pending', 0)
        .order('fecha', { ascending: true }); // FIFO

      if (err) throw err;
      
      setEntries(data || []);
    } catch (err) {
      console.error('Error loading EA entries:', err);
      setError('Error al cargar Entradas de Almacén pendientes');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(entries.map(x => x.id));
    } else {
      setSelectedIds([]);
    }
  };

  const selectedTotal = entries
    .filter(e => selectedIds.includes(e.id))
    .reduce((sum, e) => sum + Number(e.cost_pending), 0);

  const exceedsBase = selectedTotal > baseAmount;

  const handleApply = () => {
    // Si excede el monto base, aplicamos lógica FIFO hasta el tope
    let remainingToCross = baseAmount;
    const itemsToCross = [];

    // Ordenamos las seleccionadas por fecha (FIFO)
    const selectedEntries = entries
      .filter(e => selectedIds.includes(e.id))
      .sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

    for (const entry of selectedEntries) {
      if (remainingToCross <= 0) break;
      const amountToCross = Math.min(Number(entry.cost_pending), remainingToCross);
      itemsToCross.push({
        ...entry,
        applied_amount: amountToCross
      });
      remainingToCross -= amountToCross;
    }

    onApplyCross(itemsToCross);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperComponent={EADraggablePaper}>
      <DialogTitle className="ea-draggable-title" sx={{ cursor: 'move' }}>
        Cruce con Entradas de Almacén (EA)
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Proveedor: <strong>{invoice.billing_name}</strong> ({invoice.num_identificacion})
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
              <Typography variant="body2">
                Monto Base (Débitos a cruzar): <strong>{Number(baseAmount).toLocaleString('es-CO', { style: 'currency', currency: 'COP' })}</strong>
              </Typography>
              <Typography variant="body2" color={exceedsBase ? 'error' : 'primary'} fontWeight="bold">
                Total Seleccionado: {Number(selectedTotal).toLocaleString('es-CO', { style: 'currency', currency: 'COP' })}
              </Typography>
            </Box>

            {exceedsBase && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                El total seleccionado supera el monto de la factura/causación. El sistema cruzará automáticamente desde la Entrada más antigua (FIFO) hasta completar exactamente el valor base ({Number(baseAmount).toLocaleString('es-CO', { style: 'currency', currency: 'COP' })}).
              </Alert>
            )}

            {entries.length === 0 ? (
              <Typography variant="body2" color="warning.main">
                No se encontraron Entradas de Almacén pendientes para este proveedor.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 350 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox 
                          checked={entries.length > 0 && selectedIds.length === entries.length}
                          indeterminate={selectedIds.length > 0 && selectedIds.length < entries.length}
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Número</TableCell>
                      <TableCell>Doc. Ext/PO</TableCell>
                      <TableCell>Fecha</TableCell>
                      <TableCell align="right">Pendiente</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entries.map((row) => (
                      <TableRow key={row.id} hover onClick={() => handleToggle(row.id)} style={{ cursor: 'pointer' }}>
                        <TableCell padding="checkbox">
                          <Checkbox checked={selectedIds.includes(row.id)} />
                        </TableCell>
                        <TableCell>{row.tipo}</TableCell>
                        <TableCell>{row.number}</TableCell>
                        <TableCell>
                          {row.doc_externo ? `Doc: ${row.doc_externo}` : (row.po_number ? `PO: ${row.po_number}` : '-')}
                        </TableCell>
                        <TableCell>{row.fecha ? new Date(row.fecha).toLocaleDateString() : ''}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {Number(row.cost_pending).toLocaleString('es-CO', { style: 'currency', currency: 'COP' })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleApply} 
          disabled={loading || selectedIds.length === 0 || baseAmount <= 0}
        >
          Aplicar Cruce
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EACrossModal;
